/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { toast, ToastDuration } from '@sonarsource/echoes-react';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { createQueryHook, StaleTime } from '~shared/queries/common';
import {
  getSandboxInstanceSettings,
  getSandboxProjectSettings,
  updateSandboxInstanceSettings,
  updateSandboxProjectSettings,
} from './api';
import { SandboxInstanceSettings, SandboxProjectSettings } from './types';

export const useSandboxSettingsQuery = createQueryHook(() => {
  return queryOptions({
    queryKey: ['sandbox-settings', 'instance'],
    queryFn: getSandboxInstanceSettings,
    staleTime: StaleTime.NEVER,
  });
});

export const useSandboxProjectSettingsQuery = createQueryHook((projectKey: string) => {
  return queryOptions({
    queryKey: ['sandbox-settings', 'project', projectKey],
    queryFn: () => getSandboxProjectSettings(projectKey),
    staleTime: StaleTime.NEVER,
  });
});

export function useSandboxSettingsMutation() {
  const intl = useIntl();
  const client = useQueryClient();

  return useMutation({
    mutationFn: updateSandboxInstanceSettings,
    onSuccess: (data) => {
      const prevData = client.getQueryData<SandboxInstanceSettings>([
        'sandbox-settings',
        'instance',
      ]);

      toast.success({
        description: intl.formatMessage({
          id:
            !prevData?.enabled && data.enabled
              ? 'settings.sandbox_issues.enable.success'
              : 'settings.sandbox_issues.edit.success',
        }),
        duration: ToastDuration.Short,
      });

      client.setQueryData(['sandbox-settings', 'instance'], data);
      void client.invalidateQueries({ queryKey: ['sandbox-settings', 'project'] });
    },
  });
}
export function useSandboxProjectSettingsMutation(projectKey: string) {
  const intl = useIntl();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<SandboxProjectSettings>) =>
      updateSandboxProjectSettings(projectKey, data),
    onSuccess: (data) => {
      const prevData = client.getQueryData<SandboxProjectSettings>([
        'sandbox-settings',
        'project',
        projectKey,
      ]);

      // toasts differ based on the change
      let descriptionId = 'settings.sandbox_issues.project.edit.success';
      if (prevData?.enabled && !data.enabled) {
        descriptionId = 'settings.sandbox_issues.project.off.success';
      } else if (!prevData?.enabled && data.enabled) {
        descriptionId = 'settings.sandbox_issues.project.on.success';
      } else if (prevData?.overridden && !data.overridden) {
        descriptionId = 'settings.sandbox_issues.project.reset.success';
      }
      toast.success({
        description: intl.formatMessage({
          id: descriptionId,
        }),
        duration: ToastDuration.Short,
      });

      client.setQueryData(['sandbox-settings', 'project', projectKey], data);
    },
  });
}
